import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useBreakevenAnalysis, usePortfolioPnL } from '../../hooks/useCalculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { formatPrice, formatNumber, formatCurrencyCompact, pnlColorClass } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { StatusBadge } from '../common/Badge';
import { InfoButton } from '../common/Tooltip';
import { useIsMobile } from '../../hooks/useIsMobile';

interface Props {
  viewMode?: 'single' | 'portfolio';
  demandMode?: 'ceiling' | 'split';
}

export function BreakevenAnalysis({ viewMode = 'single', demandMode = 'split' }: Props) {
  const isMobile = useIsMobile();

  if (viewMode === 'portfolio') {
    return <PortfolioBreakevenView demandMode={demandMode} isMobile={isMobile} />;
  }

  return <SingleProductBreakevenView isMobile={isMobile} />;
}

function SingleProductBreakevenView({ isMobile }: { isMobile: boolean }) {
  const breakevens = useBreakevenAnalysis();
  const currentS1 = useSimulatorStore(s => s.assumptions.s1Leads);
  const data = breakevens.map(b => ({ price: formatPrice(b.price), rawPrice: b.price, s1Needed: Math.min(b.s1LeadsNeeded, 20000), actualS1Needed: b.s1LeadsNeeded, achievable: b.achievable, breakevenUnits: b.breakevenUnits }));

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Breakeven Analysis — Single Product</h3>
        <InfoButton helpKey="breakevenAnalysis" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">S1 leads needed to break even at each price (all features, full fixed costs per product). <span className="italic">S1 lead counts use base conjoint rates — your configured packages with feature lift may need fewer leads.</span></p>
      <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
        <BarChart data={data} layout="vertical" margin={{ top: 28, right: 30, left: 60, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} horizontal={false} vertical />
          <XAxis type="number" {...X_AXIS_PROPS} tickFormatter={v => formatNumber(v)} domain={[0, 20000]} />
          <YAxis type="category" dataKey="price" {...Y_AXIS_PROPS} width={55} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(_value: number, _name: string, props: any) => { const actual = props.payload.actualS1Needed; return [actual >= 999999 ? 'Never (negative margin)' : formatNumber(actual) + ' S1 leads', 'Breakeven']; }} />
          <ReferenceLine x={8501} stroke={CHART_COLORS.amber} strokeDasharray="4 4" strokeWidth={2} label={{ value: 'Max S1 (8,501)', fill: '#D97706', fontSize: 12, fontWeight: 700, position: 'top' }} />
          <ReferenceLine x={currentS1} stroke={CHART_COLORS.teal} strokeDasharray="4 4" strokeWidth={1} label={{ value: `Current (${formatNumber(currentS1)})`, fill: '#0D9488', fontSize: 12, fontWeight: 700, position: 'top' }} />
          <Bar dataKey="s1Needed" radius={[0, 4, 4, 0]} maxBarSize={28}>
            {data.map((entry, i) => <Cell key={i} fill={entry.achievable ? CHART_COLORS.emerald : CHART_COLORS.red} fillOpacity={0.7} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 space-y-2">
        {breakevens.map(b => (
          <div key={b.price} className="flex items-center justify-between text-xs">
            <span className="text-secondary font-mono">{formatPrice(b.price)}</span>
            <div className="flex items-center gap-3">
              <span className="text-primary font-mono">{b.breakevenUnits >= 999999 ? 'Negative margin' : `${formatNumber(b.breakevenUnits)} units / ${formatNumber(b.s1LeadsNeeded)} S1`}</span>
              <StatusBadge positive={b.achievable} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortfolioBreakevenView({ demandMode, isMobile }: { demandMode: 'ceiling' | 'split'; isMobile: boolean }) {
  const portfolio = usePortfolioPnL();
  const packages = useSimulatorStore(s => s.packages);
  const isCeiling = demandMode === 'ceiling';

  const totalUnits = isCeiling ? portfolio.totalS2UnitsCeiling : portfolio.totalS2UnitsSplit;
  const pnl = isCeiling ? portfolio.portfolioPnLCeiling : portfolio.portfolioPnLSplit;
  const breakevenUnits = portfolio.portfolioBreakevenUnits;
  const gap = Math.round(totalUnits) - breakevenUnits;
  const isProfitable = pnl >= 0;

  const chartData = portfolio.packages.map((pkg, i) => ({
    name: packages[i]?.name ?? `Pkg ${i + 1}`,
    contribution: isCeiling ? pkg.contributionMarginCeiling : pkg.contributionMarginSplit,
    units: Math.round(isCeiling ? pkg.s2UnitsCeiling : pkg.s2UnitsSplit),
  }));

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Portfolio Breakeven</h3>
        <InfoButton helpKey="portfolioPnL" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Fixed costs shared across all packages. Shows each package's contribution toward covering {formatCurrencyCompact(portfolio.fixedCosts)}/mo overhead.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1">Breakeven Units</div>
          <div className="text-xl font-bold text-primary font-mono">
            {breakevenUnits >= 999999 ? 'N/A' : formatNumber(breakevenUnits)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1">Current Units ({demandMode})</div>
          <div className="text-xl font-bold text-primary font-mono">{formatNumber(Math.round(totalUnits))}</div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1">Gap to Breakeven</div>
          <div className={`text-xl font-bold font-mono ${isProfitable ? 'text-metric-positive' : 'text-metric-negative'}`}>
            {breakevenUnits >= 999999 ? 'N/A' : (gap >= 0 ? '+' : '') + formatNumber(gap)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1">Portfolio P&L</div>
          <div className={`text-xl font-bold font-mono ${pnlColorClass(pnl)}`}>
            {formatCurrencyCompact(pnl)}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={isMobile ? 180 : 240}>
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
          <CartesianGrid {...GRID_PROPS} />
          <XAxis dataKey="name" {...X_AXIS_PROPS} />
          <YAxis {...Y_AXIS_PROPS} tickFormatter={v => formatCurrencyCompact(v)} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => [formatCurrencyCompact(value), 'Contribution']} />
          <ReferenceLine y={0} stroke="var(--color-reference-line)" />
          <Bar dataKey="contribution" radius={[4, 4, 0, 0]} maxBarSize={60}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.contribution >= 0 ? CHART_COLORS.emerald : CHART_COLORS.red} fillOpacity={0.7} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-4 space-y-2">
        {chartData.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-secondary">{d.name}</span>
            <div className="flex items-center gap-3">
              <span className="text-primary font-mono">{formatNumber(d.units)} units</span>
              <span className={`font-mono font-semibold ${pnlColorClass(d.contribution)}`}>{formatCurrencyCompact(d.contribution)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
